// Purpose: axios instance with request-ID interceptor and Phase 7 step-up
// handler. All API calls go through here.
//
// 428 Precondition Required is the signal from requireRecentMfa middleware
// that the caller must re-authenticate via /auth/step-up before the
// admin-side operation will run. The response interceptor catches that
// status, reads { stepUpUrl } from the body, and redirects the current
// tab — with the current pathname preserved as return_to — so admin pages
// do not have to implement the redirect themselves.

import axios, { AxiosError } from 'axios';

function randomRequestId(): string {
  // UUID v4 via crypto; falls back to Math-based for old browsers (won't happen in modern targets).
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  withCredentials: true,
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  (config.headers as Record<string, string>)['X-Request-ID'] = randomRequestId();
  return config;
});

// Callers that want to handle 428 locally (e.g. a bulk modal that wants to
// reopen after the step-up) can set this header on the individual request.
export const OPT_OUT_STEP_UP_HEADER = 'X-Ast-Handle-Step-Up-Locally';

export interface StepUpRequiredResponse {
  error: 'step_up_required';
  needsStepUp: boolean;
  stepUpUrl: string;
  maxAgeMinutes: number;
}

function isStepUpRequired(body: unknown): body is StepUpRequiredResponse {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return b['error'] === 'step_up_required' && typeof b['stepUpUrl'] === 'string';
}

// Exposed for tests; wired automatically via the response interceptor.
export function redirectToStepUp(stepUpUrl: string): void {
  if (typeof window === 'undefined') return;
  // stepUpUrl from the server already contains return_to; only rewrite it
  // when the current page is different from the original request's source
  // so the interceptor fires for calls made during page transitions.
  try {
    const parsed = new URL(stepUpUrl, window.location.origin);
    parsed.searchParams.set('return_to', window.location.pathname + window.location.search);
    window.location.assign(parsed.toString());
  } catch {
    window.location.assign(stepUpUrl);
  }
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const resp = error.response;
    if (resp && resp.status === 428 && isStepUpRequired(resp.data)) {
      const optOut =
        error.config?.headers &&
        (error.config.headers as Record<string, unknown>)[OPT_OUT_STEP_UP_HEADER] === 'true';
      if (!optOut) {
        redirectToStepUp(resp.data.stepUpUrl);
      }
    }
    return Promise.reject(error);
  }
);

// Shared CSRF helpers. Lifted from the per-file duplications in shares.ts /
// dropboxes.ts / admin-approvals.ts so new API clients inherit one copy.
export function readCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(name + '='));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

export function csrfHeaders(): Record<string, string> {
  const token = readCookie('ast_csrf');
  return token ? { 'X-CSRF-Token': token } : {};
}
